/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo } from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import type { NativeStackHeaderProps } from '@react-navigation/native-stack';
import { useWindowDimensions } from 'react-native';
import { useSessionStore } from '../session/store/useSessionStore';
import {
  ActivityHistoryScreen,
  TeamHistoryScreen,
  ArcaIntegrationScreen,
  BillingScreen,
  ActivateCreceScreen,
  CreceSuccessScreen,
  BranchSettingsScreen,
  BranchIdentityScreen,
  BranchPosOpsScreen,
  BranchDevicesScreen,
  StorefrontZoneScreen,
  DeliverySettingsScreen,
  IntegrationsHubScreen,
  EmployeePermissionsScreen,
  GlossaryScreen,
  InviteEmployeeScreen,
  MercadoPagoBranchSettingsScreen,
  OrganizationScreen,
  OwnerBranchesPanelScreen,
  TeamHubScreen,
  ChangeBusinessTypeScreen,
  BusinessFunctionsScreen,
  OperationModeScreen,
  CapabilityBreakdownScreen,
  AddFeaturesScreen,
  MarketplaceTrustScreen,
  SaleDetailScreen,
  SupportTicketDetailScreen,
  ConsultationDetailScreen,
  QuoteDetailScreen,
  AppointmentResourcesScreen,
  WorkOrderDetailScreen,
  WorkOrderFormScreen,
  CommissionsScreen,
  CommissionsHubScreen,
  PricingHubScreen,
  SettingsHubScreen,
  MyCommissionsScreen,
  VehicleFormScreen,
  ServiceRemindersScreen,
  VehicleHistoryScreen,
  FollowUpInboxScreen,
} from './screen-registry';
import { DiningTablesScreen } from '../../hospitality/screens/DiningTablesScreen';
import { KitchenQueueScreen } from '../../hospitality/screens/KitchenQueueScreen';
import ReservationsScreen from '../../hospitality/screens/ReservationsScreen';
import { KitchenTicketCountProvider } from '../../hospitality/providers/KitchenTicketCountProvider';
import { KitchenReadyAlertProvider } from '../../hospitality/providers/KitchenReadyAlertProvider';
import { GuestSignalAlertProvider } from '../../hospitality/providers/GuestSignalAlertProvider';
import { MenuOptionGroupsScreen, ProductCombosScreen, ComboCreateWizardScreen, ProductRecipeScreen, ProduceRecipeScreen, DisassembleRecipeScreen } from '../../catalog/public-api';
import { FiscalHubScreen } from '../../fiscal/screens/arca/FiscalHubScreen';
import { OnlineOrderDetailScreen } from '../../orders/screens/OnlineOrderDetailScreen'
import { OrderMessageThreadScreen } from '../../orders/screens/OrderMessageThreadScreen'
import { OrdersHistoryScreen } from '../../orders/screens/OrdersHistoryScreen';
import { ComplaintDetailScreen } from '../../complaints/public-api';
import { StoreReviewsScreen } from '../../store-reviews/screens/StoreReviewsScreen'
import { StoreReviewDetailScreen } from '../../store-reviews/screens/StoreReviewDetailScreen';
import { AdaptiveAppShell } from './AdaptiveAppShell';
import { MainTabs } from './MainTabsNavigator';
import {
  buildShellScreenOptions,
  isWebSidebarLayout,
  shellBackBehaviorForRoute,
  shellHeaderBackVisible,
} from './shellScreenOptions';
import {
  getProfileDrillDownHeaderOptions,
} from './AppNavigatorHeaders';
import { ShellOperationalHeader } from './ShellOperationalHeader';
import { TabScreenRefreshProvider } from './TabScreenRefreshContext';
import {
  SHELL_ORG_CHILD_ROUTES,
  SHELL_PROFILE_CHILD_ROUTES,
  SHELL_TEAM_CHILD_ROUTES,
  shellHeaderShowsBranch,
  shellHeaderUsesAccountChrome,
  shellHeaderUsesCompactChrome,
} from './navigationManifest';
import type { PlanEntitlementsRow } from '../feature-flags/domain/planEntitlements';
import type { GlobalFeatureFlagsMap } from '../feature-flags/domain/globalFeatureFlags';
import { shellSceneContentStyle } from './navigationTheme';
import { withScreenPageTitle } from './withScreenPageTitle';

export type { ShellStackParamList } from './shellStackParamList';
import type { ShellStackParamList } from './shellStackParamList';

const ShellStack = createNativeStackNavigator<ShellStackParamList>();

/** Shell: título en el header operativo (no H1 en el cuerpo). */
function withShellScreen<P extends object>(Screen: React.ComponentType<P>) {
  return withScreenPageTitle(Screen, { hideBodyTitle: true });
}

const ShellSaleDetail = withShellScreen(SaleDetailScreen);
const ShellOnlineOrderDetail = withShellScreen(OnlineOrderDetailScreen);
const ShellOrdersHistory = withShellScreen(OrdersHistoryScreen);
const ShellOrderMessageThread = withShellScreen(OrderMessageThreadScreen);
const ShellComplaintDetail = withShellScreen(ComplaintDetailScreen);
const ShellConsultationDetail = withShellScreen(ConsultationDetailScreen);
const ShellQuoteDetail = withShellScreen(QuoteDetailScreen);
const ShellWorkOrderDetail = withShellScreen(WorkOrderDetailScreen);
const ShellWorkOrderForm = withShellScreen(WorkOrderFormScreen);
const ShellCommissions = withShellScreen(CommissionsScreen);
const ShellCommissionsDashboard = withShellScreen(CommissionsHubScreen);
const ShellPricingHub = withShellScreen(PricingHubScreen);
const ShellSettingsHub = withShellScreen(SettingsHubScreen);
const ShellMyCommissions = withShellScreen(MyCommissionsScreen);
const ShellVehicleForm = withShellScreen(VehicleFormScreen);
const ShellServiceReminders = withShellScreen(ServiceRemindersScreen);
const ShellVehicleHistory = withShellScreen(VehicleHistoryScreen);
const ShellFollowUpInbox = withShellScreen(FollowUpInboxScreen);
const ShellAppointmentResources = withShellScreen(AppointmentResourcesScreen);
const ShellSupportTicketDetail = withShellScreen(SupportTicketDetailScreen);
const ShellOrganization = withShellScreen(OrganizationScreen);
const ShellOwnerBranchesPanel = withShellScreen(OwnerBranchesPanelScreen);
const ShellMarketplaceTrust = withShellScreen(MarketplaceTrustScreen);
const ShellChangeBusinessType = withShellScreen(ChangeBusinessTypeScreen);
const ShellBusinessFunctions = withShellScreen(BusinessFunctionsScreen);
const ShellOperationMode = withShellScreen(OperationModeScreen);
const ShellCapabilityBreakdown = withShellScreen(CapabilityBreakdownScreen);
const ShellAddFeatures = withShellScreen(AddFeaturesScreen);
const ShellBranchSettings = withShellScreen(BranchSettingsScreen);
const ShellBranchIdentity = withShellScreen(BranchIdentityScreen);
const ShellBranchPosOps = withShellScreen(BranchPosOpsScreen);
const ShellBranchDevices = withShellScreen(BranchDevicesScreen);
const ShellStorefrontHub = withShellScreen(StorefrontZoneScreen);
const ShellDeliverySettings = withShellScreen(DeliverySettingsScreen);
const ShellStoreReviews = withShellScreen(StoreReviewsScreen);
const ShellStoreReviewDetail = withShellScreen(StoreReviewDetailScreen);
const ShellIntegrationsHub = withShellScreen(IntegrationsHubScreen);
const ShellBilling = withShellScreen(BillingScreen);
const ShellActivateCrece = withShellScreen(ActivateCreceScreen);
const ShellCreceSuccess = withShellScreen(CreceSuccessScreen);
const ShellActivityHistory = withShellScreen(ActivityHistoryScreen);
const ShellTeamHistory = withShellScreen(TeamHistoryScreen);
const ShellEmployeePermissions = withShellScreen(EmployeePermissionsScreen);
const ShellInviteEmployee = withShellScreen(InviteEmployeeScreen);
const ShellArcaIntegration = withShellScreen(ArcaIntegrationScreen);
const ShellFiscalHub = withShellScreen(FiscalHubScreen);
const ShellMercadoPagoBranchSettings = withShellScreen(MercadoPagoBranchSettingsScreen);
const ShellGlossary = withShellScreen(GlossaryScreen);
const ShellDiningTables = withShellScreen(DiningTablesScreen);
const ShellReservations = withShellScreen(ReservationsScreen);
const ShellKitchenQueue = withShellScreen(KitchenQueueScreen);
const ShellMenuOptionGroups = withShellScreen(MenuOptionGroupsScreen);
const ShellProductCombos = withShellScreen(ProductCombosScreen);
const ShellComboCreateWizard = withShellScreen(ComboCreateWizardScreen);
const ShellProductRecipe = withShellScreen(ProductRecipeScreen);
const ShellProduceRecipe = withShellScreen(ProduceRecipeScreen);
const ShellDisassembleRecipe = withShellScreen(DisassembleRecipeScreen);

const SHELL_NON_MODAL_ROUTES = new Set<keyof ShellStackParamList>([
  'MainTabs',
  'SaleDetail',
  'OnlineOrderDetail',
  'OrdersHistory',
  'OrderMessageThread',
  'ComplaintDetail',
  'StoreReviews',
  'StoreReviewDetail',
  'ConsultationDetail',
  'QuoteDetail',
  'Team',
  'AppointmentResources',
]);

type AppShellLayoutProps = {
  entitlements: PlanEntitlementsRow | null;
  globalFeatureFlags: GlobalFeatureFlagsMap | null;
};

export function AppShellLayout({ entitlements, globalFeatureFlags }: AppShellLayoutProps) {
  const branches = useSessionStore((s) => s.branches);
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const currentBranch = branches.find((b) => b.id === currentBranchId);
  const { width } = useWindowDimensions();

  const shellScreenOptions = useMemo(
    () =>
      ({ route }: { route: { name: string } }) => {
        if (route.name === 'MainTabs') {
          return { headerShown: false as const };
        }
        const routeName = route.name as keyof ShellStackParamList;
        const isAccountDrillDown =
          SHELL_PROFILE_CHILD_ROUTES.has(routeName) ||
          SHELL_TEAM_CHILD_ROUTES.has(routeName) ||
          SHELL_ORG_CHILD_ROUTES.has(routeName);
        const accountHeader = shellHeaderUsesAccountChrome(routeName);
        const compactHeader = shellHeaderUsesCompactChrome(routeName);
        const showBranchInHeader = shellHeaderShowsBranch(routeName);
        const backBehavior = shellBackBehaviorForRoute(routeName);
        const showBackInHeader = shellHeaderBackVisible(width, backBehavior);
        const modalOnMobile =
          !SHELL_NON_MODAL_ROUTES.has(routeName) && !isAccountDrillDown;
        // En desktop con sidebar, las pantallas destino del sidebar no necesitan
        // header del stack (el sidebar da contexto). Mobile sí lo necesita (back).
        const hideHeaderOnWebSidebar =
          backBehavior === 'sidebarDestination' && isWebSidebarLayout(width);
        if (hideHeaderOnWebSidebar) {
          return {
            ...buildShellScreenOptions(width, { modalOnMobile, backBehavior }),
            headerShown: false as const,
            contentStyle: shellSceneContentStyle,
          };
        }
        return {
          ...buildShellScreenOptions(width, {
            modalOnMobile,
            backBehavior,
          }),
          ...(accountHeader || compactHeader ? getProfileDrillDownHeaderOptions() : {}),
          contentStyle: shellSceneContentStyle,
          header: ({ navigation, options, back }: NativeStackHeaderProps) => (
            <ShellOperationalHeader
              title={typeof options.title === 'string' ? options.title : ''}
              {...(showBranchInHeader ? { branchName: currentBranch?.name } : {})}
              showBack={showBackInHeader && back != null}
              onBack={() => navigation.goBack()}
            />
          ),
        };
      },
    [width, currentBranch?.name]
  );

  return (
    <TabScreenRefreshProvider>
      <AdaptiveAppShell>
        <KitchenTicketCountProvider>
          <KitchenReadyAlertProvider>
          <GuestSignalAlertProvider>
          <ShellStack.Navigator screenOptions={shellScreenOptions}>
        <ShellStack.Screen name="MainTabs" options={{ headerShown: false }}>
          {() => <MainTabs entitlements={entitlements} globalFeatureFlags={globalFeatureFlags} />}
        </ShellStack.Screen>
        <ShellStack.Screen
          name="SaleDetail"
          component={ShellSaleDetail}
          options={{
            title: 'Detalle de venta',
          }}
        />
        <ShellStack.Screen
          name="OnlineOrderDetail"
          component={ShellOnlineOrderDetail}
          options={{
            title: 'Pedido online',
          }}
        />
        <ShellStack.Screen
          name="OrdersHistory"
          component={ShellOrdersHistory}
          options={{
            title: 'Historial de pedidos',
          }}
        />
        <ShellStack.Screen
          name="OrderMessageThread"
          component={ShellOrderMessageThread}
          options={{
            title: 'Mensajes del pedido',
          }}
        />
        <ShellStack.Screen
          name="ComplaintDetail"
          component={ShellComplaintDetail}
          options={{
            title: 'Reclamo',
          }}
        />
        <ShellStack.Screen
          name="ConsultationDetail"
          component={ShellConsultationDetail}
          options={{
            title: 'Consulta de receta',
          }}
        />
        <ShellStack.Screen
          name="QuoteDetail"
          component={ShellQuoteDetail}
          options={{
            title: 'Cotización',
          }}
        />
        <ShellStack.Screen
          name="WorkOrderDetail"
          component={ShellWorkOrderDetail}
          options={{
            title: 'Orden de trabajo',
          }}
        />
        <ShellStack.Screen
          name="WorkOrderForm"
          component={ShellWorkOrderForm}
          options={{
            title: 'Nueva orden de trabajo',
          }}
        />
        <ShellStack.Screen
          name="Commissions"
          component={ShellCommissions}
          options={{
            // Distinto del destino del sidebar ("Comisiones" → CommissionsDashboard):
            // esta pantalla es solo comisiones por tareas de OT (UX audit #1466).
            title: 'Comisiones de OTs',
          }}
        />
        <ShellStack.Screen
          name="CommissionsDashboard"
          component={ShellCommissionsDashboard}
          options={{
            title: 'Comisiones',
          }}
        />
        <ShellStack.Screen
          name="PricingHub"
          component={ShellPricingHub}
          options={{
            title: 'Promociones y precios',
          }}
        />
        <ShellStack.Screen
          name="SettingsHub"
          component={ShellSettingsHub}
          options={{
            title: 'Configuración',
          }}
        />
        <ShellStack.Screen
          name="MyCommissions"
          component={ShellMyCommissions}
          options={{
            title: 'Mis comisiones',
          }}
        />
        <ShellStack.Screen
          name="VehicleForm"
          component={ShellVehicleForm}
          options={{
            title: 'Vehículo',
          }}
        />
        <ShellStack.Screen
          name="ServiceReminders"
          component={ShellServiceReminders}
          options={{
            title: 'Recordatorios de servicio',
          }}
        />
        <ShellStack.Screen
          name="VehicleHistory"
          component={ShellVehicleHistory}
          options={{
            title: 'Historial de servicio',
          }}
        />
        <ShellStack.Screen
          name="FollowUpInbox"
          component={ShellFollowUpInbox}
          options={{
            title: 'Seguimiento',
          }}
        />
        <ShellStack.Screen
          name="AppointmentResources"
          component={ShellAppointmentResources}
          options={{
            title: 'Configurar agendas',
          }}
        />
        <ShellStack.Screen
          name="SupportTicketDetail"
          component={ShellSupportTicketDetail}
          options={({ route }) => ({
            title: route.params?.ticketId ? 'Reporte' : 'Reporte',
          })}
        />
        <ShellStack.Screen
          name="Team"
          component={TeamHubScreen}
          options={{
            title: 'Equipo',
          }}
        />
        <ShellStack.Screen
          name="Organization"
          component={ShellOrganization}
          options={{
            title: 'Organización',
          }}
        />
        <ShellStack.Screen
          name="OwnerBranchesPanel"
          component={ShellOwnerBranchesPanel}
          options={{
            title: 'Panel del dueño',
            headerShown: false,
          }}
        />
        <ShellStack.Screen
          name="MarketplaceTrust"
          component={ShellMarketplaceTrust}
          options={{
            title: 'Estado de tu tienda online',
          }}
        />
        <ShellStack.Screen
          name="ChangeBusinessType"
          component={ShellChangeBusinessType}
          options={{
            title: 'Cambiar rubro',
          }}
        />
        <ShellStack.Screen
          name="BusinessFunctions"
          component={ShellBusinessFunctions}
          options={{
            title: 'Funciones del negocio',
          }}
        />
        <ShellStack.Screen
          name="OperationMode"
          component={ShellOperationMode}
          options={{
            title: 'Funciones del negocio',
          }}
        />
        <ShellStack.Screen
          name="CapabilityBreakdown"
          component={ShellCapabilityBreakdown}
          options={{
            title: 'Qué tenés activo',
          }}
        />
        <ShellStack.Screen
          name="AddFeatures"
          component={ShellAddFeatures}
          options={{
            title: 'Agregar funciones',
          }}
        />
        <ShellStack.Screen
          name="BranchSettings"
          component={ShellBranchSettings}
          options={{
            title: 'Ajustes de sucursal',
          }}
        />
        <ShellStack.Screen
          name="BranchIdentity"
          component={ShellBranchIdentity}
          options={{ title: 'Datos del local' }}
        />
        <ShellStack.Screen
          name="BranchPosOps"
          component={ShellBranchPosOps}
          options={{ title: 'Reglas del POS' }}
        />
        <ShellStack.Screen
          name="BranchDevices"
          component={ShellBranchDevices}
          options={{ title: 'Dispositivos' }}
        />
        <ShellStack.Screen
          name="StorefrontHub"
          component={ShellStorefrontHub}
          options={{ title: 'Tienda online' }}
        />
        <ShellStack.Screen
          name="DeliverySettings"
          component={ShellDeliverySettings}
          options={{ title: 'Delivery y zonas' }}
        />
        <ShellStack.Screen
          name="StoreReviews"
          component={ShellStoreReviews}
          options={{ title: 'Reseñas' }}
        />
        <ShellStack.Screen
          name="StoreReviewDetail"
          component={ShellStoreReviewDetail}
          options={{ title: 'Reseña' }}
        />
        <ShellStack.Screen
          name="IntegrationsHub"
          component={ShellIntegrationsHub}
          options={{ title: 'Integraciones' }}
        />
        <ShellStack.Screen
          name="Billing"
          component={ShellBilling}
          options={{ title: 'Plan y facturación' }}
        />
        <ShellStack.Screen
          name="ActivateCrece"
          component={ShellActivateCrece}
          options={{ title: 'Elegir un plan' }}
        />
        <ShellStack.Screen
          name="CreceSuccess"
          component={ShellCreceSuccess}
          options={{ title: 'Listo' }}
        />
        <ShellStack.Screen
          name="ActivityHistory"
          component={ShellActivityHistory}
          options={{ title: 'Actividad de tu cuenta' }}
        />
        <ShellStack.Screen
          name="TeamHistory"
          component={ShellTeamHistory}
          options={{ title: 'Historial del equipo' }}
        />
        <ShellStack.Screen
          name="EmployeePermissions"
          component={ShellEmployeePermissions}
          options={{ title: 'Permisos por empleado' }}
        />
        <ShellStack.Screen
          name="InviteEmployee"
          component={ShellInviteEmployee}
          options={{ title: 'Invitar empleado' }}
        />
        <ShellStack.Screen
          name="ArcaIntegration"
          component={ShellArcaIntegration}
          options={{ title: 'Facturación electrónica' }}
        />
        <ShellStack.Screen
          name="FiscalHub"
          component={ShellFiscalHub}
          options={{ title: 'Facturación electrónica' }}
        />
        <ShellStack.Screen
          name="MercadoPagoBranchSettings"
          component={ShellMercadoPagoBranchSettings}
          options={{ title: 'Mercado Pago' }}
        />
        <ShellStack.Screen
          name="Glossary"
          component={ShellGlossary}
          options={{ title: 'Glosario' }}
        />
        <ShellStack.Screen
          name="DiningTables"
          component={ShellDiningTables}
          options={{ title: 'Mesas' }}
        />
        <ShellStack.Screen
          name="Reservations"
          component={ShellReservations}
          options={{ title: 'Reservas' }}
        />
        <ShellStack.Screen
          name="KitchenQueue"
          component={ShellKitchenQueue}
          options={{ title: 'Cocina' }}
        />
        <ShellStack.Screen
          name="MenuOptionGroups"
          component={ShellMenuOptionGroups}
          options={{ title: 'Opciones de menú' }}
        />
        <ShellStack.Screen
          name="ProductCombos"
          component={ShellProductCombos}
          options={{ title: 'Combos' }}
        />
        <ShellStack.Screen
          name="ComboCreateWizard"
          component={ShellComboCreateWizard}
          options={{ title: 'Nuevo combo' }}
        />
        <ShellStack.Screen
          name="ProductRecipe"
          component={ShellProductRecipe}
          options={{ title: 'Receta' }}
        />
        <ShellStack.Screen
          name="ProduceRecipe"
          component={ShellProduceRecipe}
          options={{ title: 'Producir' }}
        />
        <ShellStack.Screen
          name="DisassembleRecipe"
          component={ShellDisassembleRecipe}
          options={{ title: 'Fraccionar' }}
        />
        </ShellStack.Navigator>
          </GuestSignalAlertProvider>
          </KitchenReadyAlertProvider>
        </KitchenTicketCountProvider>
      </AdaptiveAppShell>
    </TabScreenRefreshProvider>
  );
}
